import React, { useState, useEffect } from 'react';
import api, { API_BASE } from '../../utils/api';
import { toast } from 'sonner';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';

export default function AdminStats() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [exportPeriod, setExportPeriod] = useState('month');
  const [customStart, setCustomStart] = useState('');
  const [customEnd, setCustomEnd] = useState('');
  const [exporting, setExporting] = useState(false);

  useEffect(() => {
    async function loadStats() {
      try {
        const response = await api.get('/admin/statistics');
        setStats(response.data);
      } catch (err) {
        toast.error('Fehler beim Laden der Statistiken');
      } finally {
        setLoading(false);
      }
    }
    loadStats();
  }, []);

  async function handleExport() {
    setExporting(true);
    try {
      let url = API_BASE + '/admin/export/orders?period=' + exportPeriod;
      
      if (exportPeriod === 'custom' && customStart && customEnd) {
        url += '&start_date=' + customStart + 'T00:00:00Z';
        url += '&end_date=' + customEnd + 'T23:59:59Z';
      }
      
      const token = localStorage.getItem('token');
      const response = await fetch(url, {
        headers: {
          'Authorization': 'Bearer ' + token
        }
      });
      
      if (!response.ok) {
        throw new Error('Export fehlgeschlagen');
      }
      
      const blob = await response.blob();
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      
      const contentDisposition = response.headers.get('Content-Disposition');
      let filename = 'ED_STEEL_Bestellungen.csv';
      if (contentDisposition) {
        const match = contentDisposition.match(/filename=(.+)/);
        if (match) {
          filename = match[1];
        }
      }
      
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(downloadUrl);
      
      toast.success('CSV-Export erfolgreich heruntergeladen');
    } catch (err) {
      toast.error('Fehler beim Export: ' + err.message);
    } finally {
      setExporting(false);
    }
  }

  if (loading) {
    return (
      <div className="text-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto" />
        <p className="mt-4 text-gray-500">Statistiken werden geladen...</p>
      </div>
    );
  }

  if (!stats) {
    return <div className="text-center py-8 text-gray-500">Keine Statistiken verfügbar</div>;
  }

  const statusLabels = {
    eingegangen: { label: 'Eingegangen', color: 'bg-blue-500' },
    in_bearbeitung: { label: 'In Bearbeitung', color: 'bg-yellow-500' },
    versendet: { label: 'Versendet', color: 'bg-purple-500' },
    geliefert: { label: 'Geliefert', color: 'bg-green-500' },
    storniert: { label: 'Storniert', color: 'bg-red-500' }
  };

  const totalStatusOrders = Object.values(stats.status_counts || {}).reduce((a, b) => a + b, 0) || 1;

  return (
    <div className="space-y-8">
      {/* CSV Export Section */}
      <div className="bg-white rounded-lg border p-6">
        <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-green-600" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
            <polyline points="7 10 12 15 17 10" />
            <line x1="12" y1="15" x2="12" y2="3" />
          </svg>
          CSV-Export fuer Buchhaltung
        </h3>
        
        <div className="flex flex-wrap items-end gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Zeitraum</label>
            <select
              value={exportPeriod}
              onChange={function(e) { setExportPeriod(e.target.value); }}
              className="border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
              data-testid="export-period-select"
            >
              <option value="today">Heute</option>
              <option value="week">Letzte 7 Tage</option>
              <option value="month">Letzte 30 Tage</option>
              <option value="custom">Benutzerdefiniert</option>
            </select>
          </div>
          
          {exportPeriod === 'custom' && (
            <>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Von</label>
                <Input
                  type="date"
                  value={customStart}
                  onChange={function(e) { setCustomStart(e.target.value); }}
                  className="w-40"
                  data-testid="export-start-date"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Bis</label>
                <Input
                  type="date"
                  value={customEnd}
                  onChange={function(e) { setCustomEnd(e.target.value); }}
                  className="w-40"
                  data-testid="export-end-date"
                />
              </div>
            </>
          )}
          
          <Button
            onClick={handleExport}
            disabled={exporting || (exportPeriod === 'custom' && (!customStart || !customEnd))}
            className="bg-green-600 hover:bg-green-700"
            data-testid="export-csv-button"
          >
            {exporting ? (
              <>
                <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Exportiere...
              </>
            ) : (
              <>
                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-2" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                  <polyline points="7 10 12 15 17 10" />
                  <line x1="12" y1="15" x2="12" y2="3" />
                </svg>
                CSV herunterladen
              </>
            )}
          </Button>
        </div>
        
        <p className="text-sm text-gray-500 mt-3">
          Exportiert: Bestellnummer, Datum, Kunde, Netto/MwSt/Brutto, Versand, Zahlungsart, Status, Artikel
        </p>
      </div>

      {/* Revenue Cards */}
      <div>
        <h3 className="text-lg font-semibold mb-4">Umsatz</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="bg-gradient-to-br from-green-400 to-green-600 rounded-xl p-6 text-white shadow-lg">
            <div className="text-white/80 font-medium mb-2">Heute</div>
            <div className="text-3xl font-bold">EUR {(stats.revenue?.today || 0).toFixed(2)}</div>
            <div className="text-white/70 text-sm">{stats.orders?.today || 0} Bestellung(en)</div>
          </div>
          <div className="bg-gradient-to-br from-blue-400 to-blue-600 rounded-xl p-6 text-white shadow-lg">
            <div className="text-white/80 font-medium mb-2">Letzte 7 Tage</div>
            <div className="text-3xl font-bold">EUR {(stats.revenue?.week || 0).toFixed(2)}</div>
            <div className="text-white/70 text-sm">{stats.orders?.week || 0} Bestellung(en)</div>
          </div>
          <div className="bg-gradient-to-br from-purple-400 to-purple-600 rounded-xl p-6 text-white shadow-lg">
            <div className="text-white/80 font-medium mb-2">Letzte 30 Tage</div>
            <div className="text-3xl font-bold">EUR {(stats.revenue?.month || 0).toFixed(2)}</div>
            <div className="text-white/70 text-sm">{stats.orders?.month || 0} Bestellung(en)</div>
          </div>
        </div>
      </div>

      {/* Order Status Distribution */}
      <div>
        <h3 className="text-lg font-semibold mb-4">Bestellungen nach Status ({stats.orders?.total || 0} gesamt)</h3>
        <div className="bg-white rounded-lg border p-6">
          <div className="space-y-4">
            {Object.entries(stats.status_counts || {}).map(function(entry) {
              const status = entry[0];
              const count = entry[1];
              const percentage = (count / totalStatusOrders) * 100;
              const statusInfo = statusLabels[status] || { label: status, color: 'bg-gray-500' };
              return (
                <div key={status} className="flex items-center gap-4">
                  <div className="w-32 text-sm font-medium text-gray-700">{statusInfo.label}</div>
                  <div className="flex-1 h-8 bg-gray-100 rounded-full overflow-hidden">
                    <div 
                      className={statusInfo.color + " h-full flex items-center justify-end pe-2"}
                      style={{ width: Math.max(percentage, count > 0 ? 5 : 0) + '%' }}
                    >
                      {count > 0 && <span className="text-white text-xs font-bold">{count}</span>}
                    </div>
                  </div>
                  <div className="w-16 text-right text-sm text-gray-500">{percentage.toFixed(0)}%</div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Top Customers & Products */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div>
          <h3 className="text-lg font-semibold mb-4">Top 5 Kunden (nach Umsatz)</h3>
          <div className="bg-white rounded-lg border divide-y">
            {(stats.top_customers || []).length === 0 ? (
              <div className="p-4 text-center text-gray-500">Keine Kundendaten</div>
            ) : (
              (stats.top_customers || []).map(function(customer, index) {
                const bgColor = index === 0 ? 'bg-yellow-500' : index === 1 ? 'bg-gray-400' : index === 2 ? 'bg-amber-600' : 'bg-gray-300';
                return (
                  <div key={customer.id} className="p-4 flex items-center gap-4 hover:bg-gray-50">
                    <div className={bgColor + " w-8 h-8 rounded-full flex items-center justify-center text-white font-bold"}>
                      {index + 1}
                    </div>
                    <div className="flex-1">
                      <p className="font-medium">{customer.company_name}</p>
                      <p className="text-sm text-gray-500">{customer.contact_name}</p>
                    </div>
                    <p className="font-bold text-green-600">EUR {(customer.total_revenue || 0).toFixed(2)}</p>
                  </div>
                );
              })
            )}
          </div>
        </div>

        <div>
          <h3 className="text-lg font-semibold mb-4">Top 5 Produkte (nach Verkaeufen)</h3>
          <div className="bg-white rounded-lg border divide-y">
            {(stats.top_products || []).length === 0 ? (
              <div className="p-4 text-center text-gray-500">Keine Produktdaten</div>
            ) : (
              (stats.top_products || []).map(function(product, index) {
                const bgColor = index === 0 ? 'bg-purple-500' : index === 1 ? 'bg-purple-400' : index === 2 ? 'bg-purple-300' : 'bg-gray-300';
                return (
                  <div key={product.id} className="p-4 flex items-center gap-4 hover:bg-gray-50">
                    <div className={bgColor + " w-8 h-8 rounded-full flex items-center justify-center text-white font-bold"}>
                      {index + 1}
                    </div>
                    <div className="flex-1">
                      <p className="font-medium">{product.name}</p>
                      <p className="text-sm text-gray-500">{product.quantity}x verkauft</p>
                    </div>
                    <p className="font-bold text-purple-600">EUR {(product.revenue || 0).toFixed(2)}</p>
                  </div>
                );
              })
            )}
          </div>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-white rounded-lg border p-4">
          <div className="text-2xl font-bold">{stats.orders?.total || 0}</div>
          <div className="text-sm text-gray-500">Gesamtbestellungen</div>
        </div>
        <div className="bg-white rounded-lg border p-4">
          <div className="text-2xl font-bold">{(stats.status_counts?.eingegangen || 0) + (stats.status_counts?.in_bearbeitung || 0)}</div>
          <div className="text-sm text-gray-500">Offene Bestellungen</div>
        </div>
        <div className="bg-white rounded-lg border p-4">
          <div className="text-2xl font-bold">{stats.status_counts?.versendet || 0}</div>
          <div className="text-sm text-gray-500">Versendete</div>
        </div>
        <div className="bg-white rounded-lg border p-4">
          <div className="text-2xl font-bold">{stats.status_counts?.geliefert || 0}</div>
          <div className="text-sm text-gray-500">Abgeschlossen</div>
        </div>
      </div>
    </div>
  );
}
